import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import API, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  ShieldCheck,
  ShieldWarning,
  ShieldSlash,
  Clock,
  Download,
  FileArrowDown,
  ArrowClockwise,
  MagnifyingGlass,
  ArrowLeft,
} from "@phosphor-icons/react";
import Header from "@/components/app/Header";
import PepMatchesPanel from "@/components/app/PepMatchesPanel";

// Verdict → chip styling. Matches the AmlTab palette so a solicitor
// reading the audit log sees the same colours they see per-case.
const VERDICT_META = {
  clear: {
    label: "Clear",
    className: "bg-emerald-50 border-emerald-200 text-emerald-700",
    Icon: ShieldCheck,
  },
  requisition: {
    label: "Requisition",
    className: "bg-amber-50 border-amber-200 text-amber-800",
    Icon: ShieldWarning,
  },
  rejected: {
    label: "Rejected",
    className: "bg-rose-50 border-rose-200 text-rose-700",
    Icon: ShieldSlash,
  },
  open: {
    label: "In progress",
    className: "bg-slate-50 border-slate-200 text-slate-600",
    Icon: Clock,
  },
};

const PROVIDER_LABEL = {
  smartsearch: "SmartSearch",
  thirdfort: "Thirdfort",
  custom: "Manual",
};

// Default the date range to the current calendar year — that's what
// the SRA typically asks for on inspection. User can widen it via the
// controls at the top.
function _defaultFrom() {
  const now = new Date();
  return `${now.getFullYear()}-01-01`;
}
function _isoToday() {
  return new Date().toISOString().slice(0, 10);
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

export default function AmlAuditPage() {
  const [filters, setFilters] = useState({
    from_date: _defaultFrom(),
    to_date: _isoToday(),
    verdict: "",
    provider: "",
    q: "",
  });
  const [data, setData] = useState({ rows: [], summary: { total: 0, by_verdict: {} }, truncated: false });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);

  // Debounced text search — 400ms so we're not firing on every keypress.
  const [qDebounced, setQDebounced] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setQDebounced(filters.q), 400);
    return () => clearTimeout(t);
  }, [filters.q]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (filters.from_date) params.set("from_date", filters.from_date);
      if (filters.to_date) params.set("to_date", filters.to_date);
      if (filters.verdict) params.set("verdict", filters.verdict);
      if (filters.provider) params.set("provider", filters.provider);
      if (qDebounced) params.set("q", qDebounced);
      const { data: res } = await API.get(`/aml/audit-log?${params.toString()}`);
      setData(res);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Failed to load audit log");
    } finally {
      setLoading(false);
    }
  }, [filters.from_date, filters.to_date, filters.verdict, filters.provider, qDebounced]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Streaming CSV via a hidden anchor keeps the download in the
  // browser (rather than round-tripping through JS memory), which
  // matters when the audit log gets into the thousands of rows.
  const handleExportCsv = async () => {
    setDownloading(true);
    const tid = toast.loading("Preparing CSV…");
    try {
      const params = new URLSearchParams();
      if (filters.from_date) params.set("from_date", filters.from_date);
      if (filters.to_date) params.set("to_date", filters.to_date);
      if (filters.verdict) params.set("verdict", filters.verdict);
      if (filters.provider) params.set("provider", filters.provider);
      if (qDebounced) params.set("q", qDebounced);
      const { data: blob } = await API.get(`/aml/audit-log/export.csv?${params.toString()}`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `aml-audit-log-${_isoToday()}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded", { id: tid });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "CSV export failed", { id: tid });
    } finally {
      setDownloading(false);
    }
  };

  const summary = data.summary || { total: 0, by_verdict: {} };
  const counts = summary.by_verdict || {};

  const summaryChips = useMemo(
    () => [
      { key: "clear", label: "Clear", count: counts.clear || 0, ...VERDICT_META.clear },
      { key: "requisition", label: "Requisition", count: counts.requisition || 0, ...VERDICT_META.requisition },
      { key: "rejected", label: "Rejected", count: counts.rejected || 0, ...VERDICT_META.rejected },
      { key: "open", label: "In progress", count: counts.open || 0, ...VERDICT_META.open },
    ],
    [counts]
  );

  return (
    <div className="min-h-screen bg-slate-50" data-testid="aml-audit-page">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb */}
        <Link
          to="/aml"
          className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 mb-3"
          data-testid="aml-audit-back-link"
        >
          <ArrowLeft weight="bold" className="w-3 h-3" />
          Back to AML Dashboard
        </Link>

        <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
          <div>
            <h1
              className="text-3xl font-semibold text-slate-900"
              style={{ fontFamily: "Outfit, sans-serif" }}
              data-testid="aml-audit-title"
            >
              AML Audit Log
            </h1>
            <p className="text-sm text-slate-500 mt-1 max-w-2xl">
              Every AML check the firm has placed, across every case (including
              archived), with links to the filed PDF. Filter by date range and export
              a CSV for SRA inspections.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button
              variant="outline"
              className="gap-1.5"
              onClick={fetchData}
              disabled={loading}
              data-testid="aml-audit-refresh-btn"
            >
              <ArrowClockwise
                weight="bold"
                className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}
              />
              Refresh
            </Button>
            <Button
              className="bg-[#1462a0] hover:bg-[#10507f] text-white gap-1.5"
              onClick={handleExportCsv}
              disabled={downloading || data.rows.length === 0}
              data-testid="aml-audit-export-csv-btn"
            >
              <Download weight="bold" className="w-4 h-4" />
              {downloading ? "Exporting…" : "Export CSV"}
            </Button>
          </div>
        </div>

        {/* Summary chips */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6" data-testid="aml-audit-summary-chips">
          <div className="bg-white border border-slate-200 rounded-lg px-4 py-3">
            <p className="text-xs text-slate-500 uppercase tracking-wide">Total</p>
            <p className="text-2xl font-semibold text-slate-900 mt-0.5" data-testid="aml-audit-total-count">
              {summary.total}
            </p>
          </div>
          {summaryChips.map((c) => (
            <div
              key={c.key}
              className={`border rounded-lg px-4 py-3 ${c.className}`}
              data-testid={`aml-audit-summary-${c.key}`}
            >
              <p className="text-xs uppercase tracking-wide flex items-center gap-1">
                <c.Icon weight="fill" className="w-3 h-3" /> {c.label}
              </p>
              <p className="text-2xl font-semibold mt-0.5">{c.count}</p>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="bg-white border border-slate-200 rounded-lg p-4 mb-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-700 mb-1 block">From</label>
              <Input
                type="date"
                value={filters.from_date}
                onChange={(e) => setFilters((f) => ({ ...f, from_date: e.target.value }))}
                className="h-9 text-sm"
                data-testid="aml-audit-from-date"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-700 mb-1 block">To</label>
              <Input
                type="date"
                value={filters.to_date}
                onChange={(e) => setFilters((f) => ({ ...f, to_date: e.target.value }))}
                className="h-9 text-sm"
                data-testid="aml-audit-to-date"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-700 mb-1 block">Verdict</label>
              <Select
                value={filters.verdict || "all"}
                onValueChange={(v) => setFilters((f) => ({ ...f, verdict: v === "all" ? "" : v }))}
              >
                <SelectTrigger className="h-9 text-sm" data-testid="aml-audit-verdict-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All verdicts</SelectItem>
                  <SelectItem value="clear">Clear</SelectItem>
                  <SelectItem value="requisition">Requisition</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                  <SelectItem value="open">In progress</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-700 mb-1 block">Provider</label>
              <Select
                value={filters.provider || "all"}
                onValueChange={(v) => setFilters((f) => ({ ...f, provider: v === "all" ? "" : v }))}
              >
                <SelectTrigger className="h-9 text-sm" data-testid="aml-audit-provider-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All providers</SelectItem>
                  <SelectItem value="smartsearch">SmartSearch</SelectItem>
                  <SelectItem value="thirdfort">Thirdfort</SelectItem>
                  <SelectItem value="custom">Manual</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-700 mb-1 block">Search</label>
              <div className="relative">
                <MagnifyingGlass
                  weight="bold"
                  className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400"
                />
                <Input
                  value={filters.q}
                  onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
                  placeholder="Client name, email, or case ref"
                  className="h-9 text-sm pl-7"
                  data-testid="aml-audit-search"
                />
              </div>
            </div>
          </div>
        </div>

        {error && (
          <div className="bg-rose-50 border border-rose-200 text-rose-700 rounded-md px-4 py-2 text-sm mb-4">
            {error}
          </div>
        )}

        {/* Rows */}
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="aml-audit-table">
              <thead className="bg-slate-50 border-b border-slate-200 text-[11px] uppercase text-slate-500 tracking-wide">
                <tr>
                  <th className="text-left px-4 py-2.5">Placed</th>
                  <th className="text-left px-4 py-2.5">Case</th>
                  <th className="text-left px-4 py-2.5">Client</th>
                  <th className="text-left px-4 py-2.5">Provider</th>
                  <th className="text-left px-4 py-2.5">Verdict</th>
                  <th className="text-left px-4 py-2.5">PDF</th>
                  <th className="text-left px-4 py-2.5">Reviewed</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-slate-400 text-xs">
                      Loading…
                    </td>
                  </tr>
                ) : data.rows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={7}
                      className="px-4 py-8 text-center text-slate-400 text-xs"
                      data-testid="aml-audit-empty"
                    >
                      No AML checks match the current filter.
                    </td>
                  </tr>
                ) : (
                  data.rows.map((r) => {
                    const meta = VERDICT_META[r.verdict] || VERDICT_META.open;
                    return (
                      <tr
                        key={r.id}
                        className="hover:bg-slate-50"
                        data-testid={`aml-audit-row-${r.id}`}
                      >
                        <td className="px-4 py-2 text-xs text-slate-700 whitespace-nowrap">
                          {fmtDate(r.created_at)}
                        </td>
                        <td className="px-4 py-2">
                          <Link
                            to={`/cases/${r.case_id}?tab=aml`}
                            className="text-[#1462a0] hover:underline"
                          >
                            {r.case_ref && (
                              <span className="font-mono text-[11px] text-slate-500 mr-1">
                                {r.case_ref}
                              </span>
                            )}
                            <span className="text-sm">{r.case_title || "—"}</span>
                          </Link>
                          {r.case_deleted && (
                            <span className="ml-2 text-[10px] uppercase text-slate-400">
                              (archived)
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2 text-sm">
                          <div className="text-slate-900">{r.client_name || "—"}</div>
                          {r.client_email && (
                            <div className="text-[11px] text-slate-500">{r.client_email}</div>
                          )}
                        </td>
                        <td className="px-4 py-2 text-xs text-slate-600">
                          {PROVIDER_LABEL[r.provider] || r.provider || "—"}
                        </td>
                        <td className="px-4 py-2">
                          <span
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase border ${meta.className}`}
                            data-testid={`aml-audit-verdict-${r.id}`}
                          >
                            <meta.Icon weight="fill" className="w-2.5 h-2.5" />
                            {meta.label}
                          </span>
                        </td>
                        <td className="px-4 py-2">
                          {r.document_id ? (
                            <a
                              href={`${API.defaults.baseURL}/documents/${r.document_id}/download`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-[#1462a0] hover:underline text-xs"
                              data-testid={`aml-audit-pdf-link-${r.id}`}
                            >
                              <FileArrowDown weight="bold" className="w-3 h-3" />
                              Filed
                            </a>
                          ) : r.has_pdf ? (
                            <a
                              href={`${API.defaults.baseURL}/aml/${r.id}/report.pdf`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-slate-500 hover:text-[#1462a0] text-xs"
                              data-testid={`aml-audit-pdf-live-${r.id}`}
                            >
                              <FileArrowDown weight="bold" className="w-3 h-3" />
                              Fetch
                            </a>
                          ) : (
                            <span className="text-xs text-slate-400">—</span>
                          )}
                        </td>
                        <td className="px-4 py-2 text-xs" data-testid={`aml-audit-reviewed-${r.id}`}>
                          {r.last_downloaded_by ? (
                            <>
                              <div className="text-slate-900 font-medium">{r.last_downloaded_by}</div>
                              <div className="text-[11px] text-slate-500">
                                {fmtDate(r.last_downloaded_at)}
                                {r.download_count > 1 && (
                                  <span className="ml-1.5 text-[10px] text-slate-400">
                                    · {r.download_count} downloads
                                  </span>
                                )}
                              </div>
                            </>
                          ) : (
                            <span className="text-slate-400">Not yet reviewed</span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          {data.truncated && (
            <div className="px-4 py-2 border-t border-slate-100 bg-amber-50 text-amber-800 text-xs" data-testid="aml-audit-truncated-notice">
              Showing the most recent 500 rows. Narrow the date range or export as CSV for the full trail.
            </div>
          )}
        </div>

        {/* Firm-wide PEP / Sanctions / RCA / SIP matches — auto-hides
            when nothing is flagged. Sits below the audit table so the
            main table is still the first thing you see. */}
        <PepMatchesPanel />
      </div>
    </div>
  );
}
